import { AnalyticsService } from '@alison/analytics/client';
import Box from '@mui/material/Box';
import { type ReactNode, useMemo, useState } from 'react';
import { dateWindowFromRange } from '../../lib/dateUtils';
import { type AddedFilter, type FilterSearchRoute, useFilterSearch } from '../../lib/filter-search';
import {
  type DateWindow,
  type NameDimension,
  type PanelDimensions,
  type PanelValues,
  type SelectedFilters,
  useFilterCatalog,
  useNameValues,
  usePanelValues,
} from '../../lib/filters.hooks';
import { AddFilterMenu } from './AddFilterMenu';
import { FilterControl } from './FilterControl';
import { type AnyFilterKey, type FilterDefinition, filterDefinition } from './filterCatalog';
import { withJustAdded } from './justAdded';
import { MetricControl } from './MetricControl';
import { FILTER_BAR_ROW_SX } from './menuStyles';
import { ScopeLabel } from './ScopeLabel';
import { ScopeRow } from './ScopeRow';
import type { ScopeOption, ScopeState } from './types';
import { useScopeAccounts, useScopeSelection } from './useScopeSelection';

export const CREATIVE_TYPE_PANEL_KEYS: PanelDimensions = ['creativeType'];

export function creativeTypeOptions(panelValues: PanelValues | undefined): ScopeOption[] {
  return (panelValues?.creativeType?.values ?? [])
    .map((value): ScopeOption => ({ id: value, label: value }))
    .sort((first, second) => first.label.localeCompare(second.label));
}

// Metric-condition filtering (ROAS/CTR/CPA thresholds) has no backend home on the creatives
// endpoints Explore uses yet, so the UI entry point is hidden everywhere for now rather than
// removed — flip this back to true once that lands and the Explore board applies it.
const METRIC_FILTER_ENABLED = false;

export type FilterBar = 'workflows' | 'explore' | 'elements-explorer';

interface WorkflowsFilterBarProps {
  bar: FilterBar;
  to?: FilterSearchRoute;
  scope?: ScopeState;
  onScopeChange?: (scope: ScopeState) => void;
}

interface WorkflowsFilterBarBodyProps {
  bar: FilterBar;
  to: FilterSearchRoute;
  scope: ScopeState;
  applyScope: (scope: ScopeState) => void;
  scopeNetworks: string[];
  networkLabels: Record<string, string>;
  accountIds: number[];
}

function NameFilterControl({
  filter,
  definition,
  dateWindow,
  accountIds,
  selected,
  autoOpen,
  onChange,
  onRemove,
}: {
  filter: AddedFilter;
  definition: FilterDefinition;
  dateWindow: DateWindow;
  accountIds: number[];
  selected: SelectedFilters;
  autoOpen: boolean;
  onChange: (values: string[]) => void;
  onRemove: () => void;
}): ReactNode {
  const { data, isFetching, onSearchChange } = useNameValues(
    filter.key as NameDimension,
    dateWindow,
    accountIds,
    selected,
  );
  const bucket = data?.[filter.key as NameDimension];
  return (
    <FilterControl
      label={definition.label}
      family={definition.family}
      options={bucket?.values ?? []}
      values={filter.values}
      onChange={onChange}
      onRemove={onRemove}
      onSearchChange={onSearchChange}
      autoOpen={autoOpen}
      isLoading={isFetching}
      truncated={bucket?.truncated ?? false}
    />
  );
}

function WorkflowsFilterBarBody({
  bar,
  to,
  scope,
  applyScope,
  scopeNetworks,
  networkLabels,
  accountIds,
}: WorkflowsFilterBarBodyProps): ReactNode {
  const [justAdded, setJustAdded] = useState<AnyFilterKey | 'metric' | null>(null);
  const {
    added,
    metric,
    addFilter,
    removeFilter,
    patchFilter,
    addMetric,
    setMetricConditions,
    removeMetric,
  } = useFilterSearch(to);

  const dateWindow = dateWindowFromRange(scope.range, new Date());
  const activeKeys = new Set(added.map((filter) => filter.key));
  const { data: catalog, isPending: catalogPending } = useFilterCatalog(accountIds);
  const catalogByKey = useMemo(
    () => new Map((catalog ?? []).map((entry) => [entry.key, entry])),
    [catalog],
  );
  // A filter whose options come from the server narrows the others; the app's own buckets do not.
  const selected = useMemo(
    () =>
      Object.fromEntries(
        added
          .filter(
            (filter) =>
              filterDefinition(filter.key).staticOptions === undefined &&
              filter.values.length === 1,
          )
          .map((filter) => [filter.key, filter.values[0]]),
      ) as SelectedFilters,
    [added],
  );
  const { data: panelValues, isPending: panelValuesPending } = usePanelValues(
    undefined,
    selected,
    dateWindow,
    accountIds,
  );
  const { data: creativeTypePanelValues } = usePanelValues(
    CREATIVE_TYPE_PANEL_KEYS,
    undefined,
    dateWindow,
    accountIds,
  );

  const onAddMetric = (): void => {
    setJustAdded('metric');
    addMetric();
  };

  const onAddFilter = (key: AnyFilterKey): void => {
    setJustAdded(key);
    addFilter(key);
  };

  return (
    <Box sx={FILTER_BAR_ROW_SX}>
      <ScopeLabel title="The data every workflow runs over. Each workflow's own Configure step sets how that data is analyzed." />
      <ScopeRow
        scope={scope}
        onChange={applyScope}
        creativeTypeOptions={creativeTypeOptions(creativeTypePanelValues)}
      />

      {METRIC_FILTER_ENABLED && metric ? (
        <MetricControl
          conditions={metric.conditions}
          onChange={setMetricConditions}
          onRemove={removeMetric}
          autoOpen={justAdded === 'metric'}
          accountIds={accountIds}
        />
      ) : null}

      {withJustAdded(added, justAdded === 'metric' ? null : justAdded).map((filter) => {
        const served = catalogByKey.get(filter.key);
        const definition = filterDefinition(filter.key, served?.label);
        const bucket = panelValues?.[filter.key];
        const valueLabels = bucket?.labels;
        const onChange = (values: string[]): void => {
          AnalyticsService.track('Filter - Changed', {
            bar,
            filter: filter.key,
            values: values.map((value) => valueLabels?.[value] ?? value),
          });
          patchFilter(filter.key, values);
        };
        const onRemove = (): void => {
          if (justAdded === filter.key) setJustAdded(null);
          removeFilter(filter.key);
        };
        const autoOpen = justAdded === filter.key;

        if (served?.valuesVia === 'filter_names') {
          return (
            <NameFilterControl
              key={filter.key}
              filter={filter}
              definition={definition}
              dateWindow={dateWindow}
              accountIds={accountIds}
              selected={selected}
              onChange={onChange}
              onRemove={onRemove}
              autoOpen={autoOpen}
            />
          );
        }

        const options = definition.staticOptions
          ? [...definition.staticOptions]
          : (bucket?.values ?? []);
        const serverOptions = definition.staticOptions === undefined;

        return (
          <FilterControl
            key={filter.key}
            label={definition.label}
            family={definition.family}
            options={options}
            {...(bucket?.labels ? { valueLabels: bucket.labels } : {})}
            values={filter.values}
            onChange={onChange}
            onRemove={onRemove}
            autoOpen={autoOpen}
            isLoading={
              serverOptions && (panelValuesPending || (served === undefined && catalogPending))
            }
            truncated={bucket?.truncated ?? false}
          />
        );
      })}

      <AddFilterMenu
        activeKeys={activeKeys}
        metricAdded={metric !== null}
        showMetric={METRIC_FILTER_ENABLED}
        scopeNetworks={scopeNetworks}
        networkLabels={networkLabels}
        panelValues={panelValues}
        accountIds={accountIds}
        offerTenantFilters={bar !== 'workflows'}
        onAddMetric={onAddMetric}
        onAddFilter={onAddFilter}
      />
    </Box>
  );
}

// Explore passes down a scope/onScopeChange pair that BoardPage already manages through its
// own useScopeSelection instance — cookie sync, analytics, and query invalidation for it are
// already handled there. This branch only needs the pure scope-derived data alongside it, via
// the side-effect-free useScopeAccounts, rather than a second full useScopeSelection instance
// that would run every one of those side effects again on top of BoardPage's.
function ControlledWorkflowsFilterBar({
  bar,
  to,
  scope,
  onScopeChange,
}: {
  bar: FilterBar;
  to: FilterSearchRoute;
  scope: ScopeState;
  onScopeChange: (scope: ScopeState) => void;
}): ReactNode {
  const { scopeNetworks, networkLabels, accountIds } = useScopeAccounts(scope);
  return (
    <WorkflowsFilterBarBody
      bar={bar}
      to={to}
      scope={scope}
      applyScope={onScopeChange}
      scopeNetworks={scopeNetworks}
      networkLabels={networkLabels}
      accountIds={accountIds}
    />
  );
}

// The bare /workflows usage owns its scope end-to-end, so it's the one case that needs the
// full, side-effecting useScopeSelection instance.
function UncontrolledWorkflowsFilterBar({
  bar,
  to,
}: {
  bar: FilterBar;
  to: FilterSearchRoute;
}): ReactNode {
  const { scope, applyScope, scopeNetworks, networkLabels, accountIds } = useScopeSelection();
  return (
    <WorkflowsFilterBarBody
      bar={bar}
      to={to}
      scope={scope}
      applyScope={applyScope}
      scopeNetworks={scopeNetworks}
      networkLabels={networkLabels}
      accountIds={accountIds}
    />
  );
}

export function WorkflowsFilterBar({
  bar,
  to = '.',
  scope,
  onScopeChange,
}: WorkflowsFilterBarProps): ReactNode {
  if (scope !== undefined && onScopeChange !== undefined) {
    return (
      <ControlledWorkflowsFilterBar bar={bar} to={to} scope={scope} onScopeChange={onScopeChange} />
    );
  }
  return <UncontrolledWorkflowsFilterBar bar={bar} to={to} />;
}
